import { memo } from 'react';

import { cn } from '@/lib/utils';
import { useAppTheme } from '@/theme/themeProvider';

/**
 * Professional AppLoading component for corporate/banking applications
 * Features clean, formal design with enterprise-grade styling
 */
export const AppLoading = memo(() => {
  const { isDark } = useAppTheme();

  // Simple theme-specific classes
  const textClasses = isDark ? 'text-slate-100' : 'text-slate-900';
  const subtitleClasses = isDark ? 'text-slate-400' : 'text-slate-600';

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center',
        isDark ? 'bg-slate-900' : 'bg-white',
      )}
    >
      {/* Loading container */}
      <div
        className={cn(
          'flex flex-col items-center space-y-6 p-8',
          'rounded-xl border',
          isDark
            ? 'border-slate-700 bg-slate-800'
            : 'border-slate-200 bg-white',
        )}
      >
        {/* App Logo */}
        <div className="flex flex-col items-center space-y-3">
          <div className="relative">
            <img
              src="/images/logo.jpg"
              alt="Healthy Empire"
              className="h-12 w-auto object-contain"
              onError={(e) => {
                // Fallback to a simple icon if image fails to load
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent) {
                  parent.innerHTML = `
                    <div class="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-green-500 to-green-600 shadow-lg">
                      <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
                      </svg>
                    </div>
                  `;
                }
              }}
            />
          </div>
          <div className="text-center">
            <h1 className={cn('text-lg font-semibold', textClasses)}>
              Healthy Zone
            </h1>
            <p className={cn('text-xs font-medium', subtitleClasses)}>
              النظام الذكي لإدارة شركات الدواء
            </p>
          </div>
        </div>

        {/* Simple loading spinner */}
        <div className="h-8 w-8">
          <div
            className={cn(
              'h-full w-full rounded-full border-4',
              isDark
                ? 'border-slate-700 border-t-blue-400'
                : 'border-slate-200 border-t-blue-600',
            )}
          />
        </div>

        {/* Loading text */}
        <div className="space-y-2 text-center">
          <h2 className={cn('text-base font-semibold', textClasses)}>
            جاري تحميل النظام
          </h2>
          <p className={cn('text-sm font-medium', subtitleClasses)}>
            يرجى الانتظار
          </p>
        </div>
      </div>
    </div>
  );
});

AppLoading.displayName = 'AppLoading';
